import type { SearchParams } from '@/hooks/useRouterQuery';

import type { TeamLogo } from '@poseidon/components/Avatar';
import type { CompanyOption } from '@poseidon/components/Input2';
import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import { useEffect, useState } from 'react';
import type { FilterShow, OffsetPaginationType } from './types';
import { humanizeCandidateStatus } from '@/clientOnlyUtils/humanizeCandidateStatus';
import { useAuthContext } from '~/auth/auth.context';
import { useFilterDefaults } from '@/hooks/useFilterDefaults';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { getSettingsPermissions } from '~/hooks/settingsPermissions';
import { api } from '@/utils/api';
import { OffsetPaginator } from '~/components/OffsetPaginator';
import { FilterChip } from './FilterChip';
import { PickFilterButtonPure } from './PickFilterButtonPure';
import { DEFAULT_LIMIT, formatDate, resolveDateRange } from './utils';

export type CandidateStatusValue =
  | 'pending'
  | 'researching'
  | 'research_failed'
  | 'gathering_sources'
  | 'synthesizing'
  | 'synthesize_failed'
  | 'filter_failed'
  | 'personalize_failed'
  | 'rate_limited'
  | 'account_disconnected'
  | 'awaiting_capacity'
  | 'retrying'
  | 'pending_review'
  | 'rejected'
  | 'promoted'
  | 'removed';

// Filterable subset — we hide `personalizing` and `removed` because they
// don't make sense as standalone filter buckets from the staging UI. The
// blocked statuses ARE filterable — "show me everything that's rate-limited"
// is exactly the query this redesign enables. The three VERDICT buckets sit
// together at the end (Needs review / Rejected / Enrolled) — with the
// auto-loader they're the reviewer's main lenses. Labels come from the
// shared humanizer so a rename flows everywhere.
export const CANDIDATE_STATUS_OPTIONS: { value: CandidateStatusValue; label: string }[] = (
  ['pending', 'researching', 'research_failed', 'gathering_sources', 'synthesizing', 'synthesize_failed', 'filter_failed', 'personalize_failed', 'rate_limited', 'account_disconnected', 'awaiting_capacity', 'retrying', 'pending_review', 'rejected', 'promoted'] as const
).map((v) => ({ value: v, label: humanizeCandidateStatus(v) }));

export const CANDIDATE_STATUS_ALL_VALUE = '';

interface Filters2Props {
  teamId: string;
  teamName: string;
  userId: string;
  teamLogo?: TeamLogo | null;
  pagination?: OffsetPaginationType;
  show: FilterShow[];
  className?: string;
  end?: React.ReactNode;
  orgOptions?: CompanyOption[];
  allowOwnerFilterForAll?: boolean;
  /**
   * When set, the contact-owner (`userId`) filter renders with this label in a
   * ButtonGroup, parallel to the `cadenceUserId` "Campaign owner" group. Used on
   * Connection Results (where it's "Connected" — whose LinkedIn connections you're
   * viewing) to disambiguate it from the campaign-owner filter. Left undefined on
   * other pages, where the bare owner dropdown is shown.
   */
  userIdLabel?: string;
  /**
   * Counts to display next to each tab when `'candidateStatus'` is in `show`.
   * Keyed by candidate status; missing keys render as `(0)`.
   */
  candidateStatusCounts?: Partial<Record<CandidateStatusValue, number>>;
  /**
   * Counts for the Active/All enrollment toggle when `'enrollment'` is in `show`.
   * `active` = cadences with is_active = true; `all` = every non-deleted cadence.
   */
  enrollmentCounts?: { active: number; all: number };
}

export default function Filters2({
  teamId,
  teamName,
  userId,
  teamLogo,
  pagination,
  show,
  className,
  end,
  orgOptions,
  allowOwnerFilterForAll = false,
  userIdLabel,
  candidateStatusCounts,
  enrollmentCounts,
}: Filters2Props) {
  const [query, , updateQuery] = useRouterQuery();
  const { fromDate, toDate } = resolveDateRange(query);

  // Permission gate: only admins/superadmins (incl. impersonating superadmins) can
  // change the owner filter. Regular users always see only their own data — the
  // dropdown is hidden and the URL is normalized to their own userId.
  const [auth] = useAuthContext();
  const userTeam = 'userTeam' in auth ? auth.userTeam : null;
  const { isAdmin } = getSettingsPermissions(
    {
      userId,
      isAdmin: userTeam?.isAdmin ?? false,
      isSuperAdmin: userTeam?.isSuperAdmin ?? false,
    },
    userId,
    auth.impersonation != null,
  );
  const canSeeOwnerFilter = isAdmin || allowOwnerFilterForAll;
  const effectiveShow: FilterShow[] = canSeeOwnerFilter
    ? show
    : show.filter((f) => f !== 'userId' && f !== 'cadenceUserId');
  const { saveDefaults, clearDefaults } = useFilterDefaults(teamId, effectiveShow);

  // For non-admins, force `userId` query param to their own id so all downstream
  // consumers (cadences, contacts, etc.) only fetch their own data. Opt-out via
  // `allowOwnerFilterForAll` for shared-resource pages (e.g. Content Library).
  useEffect(() => {
    if (!canSeeOwnerFilter && show.includes('userId') && query.userId !== userId) {
      updateQuery({ userId });
    }
  }, [canSeeOwnerFilter, show, query.userId, userId, updateQuery]);

  const showsUserId = effectiveShow.includes('userId');
  const showsCadenceUserId = effectiveShow.includes('cadenceUserId');
  const showsConnectedUserId = show.includes('connectedUserId');
  const showsTag = show.includes('tag') || show.includes('contactTag');
  const showsTemplateId = show.includes('templateId');
  const showsStage = show.includes('stageId') || show.includes('stageId2') || show.includes('stageIds');

  const filtersQuery = api.filters.data.useQuery({
    teamId,
    includeUserOptions: showsUserId || showsCadenceUserId,
    includeTeamUsers: showsConnectedUserId,
    includeTags: showsTag,
    includeTemplates: showsTemplateId,
    includeStages: showsStage,
  });

  const userOptions = filtersQuery.data?.userOptions ?? [];
  const tagOptions = filtersQuery.data?.tags?.map((tag) => ({ value: tag.id, label: tag.name })) ?? [];
  const contactTagOptions =
    filtersQuery.data?.tags
      ?.filter((tag) => tag.contactOnly)
      .map((tag) => ({ value: tag.id, label: tag.name })) ?? [];
  const templates =
    filtersQuery.data?.templates?.map((d) => ({
      id: d.id,
      name: d.name,
      userId: d.userId,
    })) ?? [];
  const stages = filtersQuery.data?.stages ?? [];

  const [selectedFilter, setSelectedFilter] = useState<FilterShow | null>(null);

  const updateFilters = (changes: Partial<SearchParams>) => {
    const newQuery = { ...query, ...changes, offset: undefined };
    updateQuery({ ...changes, offset: undefined });
    saveDefaults(newQuery);
  };

  const handleDateRangeChange = ({ from, to }: { from?: string; to?: string }) => {
    const nextFrom = from ?? formatDate(fromDate);
    const nextTo = to ?? formatDate(toDate);
    const nextRange = `${nextFrom}.${nextTo}`;
    updateFilters({ dateRange: nextRange, from: undefined, to: undefined });
  };

  const isFilterActive = (filter: FilterShow) => {
    switch (filter) {
      case 'userId':
      case 'dateRange':
        return true;
      case 'connectedUserId':
        return query.connectedUserId != null;
      case 'connectionAccepted':
        return query.connectionAccepted != null;
      case 'connectionType':
        return query.connectionType != null && query.connectionType !== 'all';
      case 'stepsRunCount':
        return query.stepsRunCount != null;
      case 'tag':
      case 'contactTag':
        return query.tag != null;
      case 'templateId':
        return query.templateId != null;
      case 'stageId':
        return query.stageId != null;
      case 'stageId2':
        return query.stageId != null;
      case 'stageIds':
        return query.stageIds != null && query.stageIds.length > 0;
      case 'signalBuckets':
        return query.signalBuckets != null && query.signalBuckets.length > 0;
      case 'order':
        return query.order != null;
      case 'states':
        return query.states != null;
      case 'response':
        return query.response != null;
      case 'companyId':
        return query.companyId != null;
      case 'templateSort':
        return true;
      case 'recentlyConnected':
        return query.recentlyConnected === true;
      case 'messagesSentMax':
        return query.messagesSentMax != null;
      case 'candidateStatus':
        // Always show the candidate-status tabs in the toolbar; the "All" tab
        // represents the no-filter state.
        return true;
      case 'templateActivity':
        // Always show — the "All" option represents the no-filter state.
        return true;
      default:
        return false;
    }
  };

  const ALWAYS_VISIBLE_FILTERS: ReadonlySet<FilterShow> = new Set([
    'userId',
    'cadenceUserId',
    'dateRange',
    'stageId',
    'stageId2',
    'templateId',
    'templateSort',
    'candidateStatus',
    'templateActivity',
    'enrollment',
  ]);

  const activeFilters = effectiveShow.filter(
    (filter) => ALWAYS_VISIBLE_FILTERS.has(filter) || isFilterActive(filter),
  );
  const availableFilters = effectiveShow.filter(
    (filter) => !ALWAYS_VISIBLE_FILTERS.has(filter) && !isFilterActive(filter),
  );

  const hasFilters = effectiveShow.some((key) => {
    if (key === 'userId' || key === 'cadenceUserId' || key === 'dateRange' || key === 'templateId') {
      return false;
    }
    if (key === 'stageId' || key === 'stageId2') {
      return query.stageId != null;
    }
    if (key === 'stageIds') {
      return query.stageIds != null && query.stageIds.length > 0;
    }
    if (key === 'signalBuckets') {
      return query.signalBuckets != null && query.signalBuckets.length > 0;
    }
    return isFilterActive(key);
  });
  const hasUnselectedFilters = availableFilters.length > 0;

  useEffect(() => {
    if (selectedFilter && !availableFilters.includes(selectedFilter)) {
      setSelectedFilter(null);
    }
  }, [availableFilters, selectedFilter]);

  const clearFilters = () => {
    const clearObj = Object.fromEntries(effectiveShow.map((k) => [k, undefined])) as Partial<SearchParams>;
    // Also clear stepsRunCountOperation when clearing stepsRunCount
    if (effectiveShow.includes('stepsRunCount')) {
      clearObj.stepsRunCountOperation = undefined;
    }
    if (effectiveShow.includes('stageId2')) {
      clearObj.stageId = undefined;
    }
    if (effectiveShow.includes('stageIds')) {
      clearObj.stageIds = undefined;
    }
    if (effectiveShow.includes('signalBuckets')) {
      clearObj.signalBuckets = undefined;
    }
    updateQuery({ ...clearObj, offset: undefined });
    clearDefaults();
  };

  return (
    <nav
      className={cn(
        'flex flex-row border-t border-b p-2 px-8 gap-1 bg-slate-100  border-slate-200 dark:bg-charade-950 dark:border-charade-900',
        className,
      )}>
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter}
          filter={filter}
          teamName={teamName}
          userId={userId}
          teamLogo={teamLogo}
          userIdLabel={userIdLabel}
          query={query}
          userOptions={userOptions}
          tagOptions={tagOptions}
          contactTagOptions={contactTagOptions}
          templates={templates}
          stages={stages}
          orgOptions={orgOptions}
          teamUsers={filtersQuery.data?.teamUsers ?? null}
          filtersLoading={filtersQuery.isLoading}
          fromDate={fromDate}
          toDate={toDate}
          candidateStatusCounts={candidateStatusCounts}
          enrollmentCounts={enrollmentCounts}
          onUpdateFilters={updateFilters}
          onDateRangeChange={handleDateRangeChange}
        />
      ))}

      {hasUnselectedFilters && (
        <PickFilterButtonPure
          teamId={teamId}
          teamName={teamName}
          userId={userId}
          teamLogo={teamLogo}
          availableFilters={availableFilters}
          orgOptions={orgOptions}
          selectedFilter={selectedFilter}
          onSelectFilter={setSelectedFilter}
          onFilterChange={(changes) => {
            updateFilters(changes);
            setSelectedFilter(null);
          }}
          tags={filtersQuery.data?.tags ?? []}
          teamUsers={filtersQuery.data?.teamUsers ?? []}
          templates={templates}
          stages={stages}
        />
      )}

      {hasFilters && (
        <Button size="sm" icon={<General.XMark />} text onClick={clearFilters}>
          Clear
        </Button>
      )}
      <div className="grow" />
      {end}
      {pagination && (
        <OffsetPaginator
          limit={query.limit}
          defaultLimit={DEFAULT_LIMIT}
          offset={pagination.offset}
          len={pagination.len}
          onChange={(next) => updateQuery(next)}
        />
      )}
    </nav>
  );
}
